import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaHome, FaTrophy, FaMoon, FaSun, FaDownload, FaSignInAlt, FaUser } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';

function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  // Check if app is running in PWA mode
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone || 
                        document.referrer.includes('android-app://');
    setIsPWA(isStandalone);
  }, []);

  // Check if user is logged in
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('userToken');
      setIsLoggedIn(!!token);
    };
    
    checkLoginStatus();
    // Listen for storage changes (logout in another tab)
    window.addEventListener('storage', checkLoginStatus);
    // Check on every route change
    checkLoginStatus();
    
    return () => window.removeEventListener('storage', checkLoginStatus);
  }, [location]);

  const navItems = [
    { path: '/', label: 'خانه', icon: <FaHome /> },
    { path: '/festivals', label: 'جشنواره‌ها', icon: <FaTrophy /> },
  ];

  return (
    <header className="fixed top-0 w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-gov dark:shadow-xl transition-colors duration-300 z-50" data-testid="navbar">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-3">
          {/* Logo - نوآفرین‌شو Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <motion.img 
              src="https://customer-assets.emergentagent.com/job_logo-update-11/artifacts/aj06zws1_Logo%20NOAFARIN%20%20SHO.png" 
              alt="لوگو نوآفرین‌شو" 
              className="h-14 w-14 md:h-16 md:w-16 object-contain group-hover:scale-110 transition-transform duration-300"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
            />
            <div>
              <h1 className="text-2xl md:text-3xl logo-special-font text-gradient-blue dark:bg-gradient-to-r dark:from-blue-400 dark:to-cyan-300 dark:bg-clip-text dark:text-transparent">
                نوآفرین<span className="text-secondary-500 dark:text-secondary-400 logo-special-font text-3xl md:text-4xl">شو</span>
              </h1>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 font-semibold">سامانه ثبت طرح‌های فناور</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2">
            {/* Dark Mode Toggle */}
            <motion.button
              onClick={toggleTheme}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-3 rounded-xl bg-gradient-to-r from-primary-100 to-primary-50 dark:from-gray-700 dark:to-gray-600 hover:from-primary-200 hover:to-primary-100 dark:hover:from-gray-600 dark:hover:to-gray-500 transition-all shadow-md mr-2"
              aria-label="تغییر حالت تاریک/روشن"
            >
              {isDarkMode ? (
                <FaSun className="text-2xl text-yellow-400" />
              ) : (
                <FaMoon className="text-2xl text-primary-700" />
              )}
            </motion.button>

            {/* Login/Dashboard Button */}
            {isLoggedIn ? (
              <Link
                to="/user/dashboard"
                className="relative px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 flex items-center gap-2 bg-gradient-to-r from-accent-600 to-accent-500 text-white shadow-lg hover:shadow-xl hover:from-accent-700 hover:to-accent-600"
                data-testid="dashboard-btn"
              >
                <FaUser className="text-lg" />
                داشبورد
              </Link>
            ) : (
              <Link
                to="/user/login"
                className="relative px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 flex items-center gap-2 bg-gradient-to-r from-accent-600 to-accent-500 text-white shadow-lg hover:shadow-xl hover:from-accent-700 hover:to-accent-600"
                data-testid="login-btn"
              >
                <FaSignInAlt className="text-lg" />
                ورود
              </Link>
            )}

            {/* Download App Button - Only show in browser mode, not in PWA */}
            {!isPWA && (
              <Link
                to="/download-app"
                className={`relative px-6 py-3 rounded-xl font-bold text-base transition-all duration-300 flex items-center gap-2 ${
                  location.pathname === '/download-app'
                    ? 'bg-gradient-to-r from-secondary-600 to-secondary-500 text-gray-900 shadow-lg'
                    : 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-gray-900 hover:from-secondary-600 hover:to-secondary-700 shadow-md hover:shadow-lg'
                }`}
                data-testid="download-app-btn"
              >
                <FaDownload className="text-lg" />
                دانلود اپلیکیشن
              </Link>
            )}

            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 lg:px-6 xl:px-8 py-3 rounded-xl font-bold text-base lg:text-lg transition-all duration-300 flex items-center gap-2 ${
                  location.pathname === item.path
                    ? 'bg-gradient-to-r from-primary-700 to-primary-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-200 hover:text-primary-700 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {item.icon}
                {item.label}
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute inset-0 bg-gradient-to-r from-primary-700 to-primary-600 rounded-xl -z-10"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </Link>
            ))}
            
            {/* Novayar Link - Separate */}
            <Link
              to="/novayar"
              className={`relative px-4 lg:px-6 xl:px-8 py-3 rounded-xl font-bold text-base lg:text-lg transition-all duration-300 flex items-center gap-2 ${
                location.pathname === '/novayar'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : 'text-gray-700 dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-xl">🤖</span>
              دستیار هوشمند نوآیار
              {location.pathname === '/novayar' && (
                <motion.div
                  layoutId="activeNavNovayar"
                  className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl -z-10"
                  transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                />
              )}
            </Link>
          </nav>

          {/* Mobile Controls */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Dark Mode Toggle Mobile */}
            <motion.button
              onClick={toggleTheme}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="p-3 rounded-xl bg-gradient-to-r from-primary-100 to-primary-50 dark:from-gray-700 dark:to-gray-600 transition-all"
              aria-label="تغییر حالت تاریک/روشن"
            >
              {isDarkMode ? (
                <FaSun className="text-xl text-yellow-400" />
              ) : (
                <FaMoon className="text-xl text-primary-700" />
              )}
            </motion.button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-3 rounded-xl bg-primary-50 dark:bg-gray-700 hover:bg-primary-100 dark:hover:bg-gray-600 transition-all"
              aria-label="منوی موبایل"
            >
              {isMenuOpen ? (
                <FaTimes className="text-2xl text-primary-700 dark:text-gray-200" />
              ) : (
                <FaBars className="text-2xl text-primary-700 dark:text-gray-200" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
            >
              <div className="py-4 space-y-2">
                {/* New Order for Mobile Menu:
                    1. خانه (Home)
                    2. ورود/داشبورد (Login/Dashboard based on login status)
                    3. دانلود اپلیکیشن (Download App)
                    4. جشنواره امید (Festival)
                */}
                
                {/* 1. Home Link */}
                <Link
                  to="/"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all ${
                    location.pathname === '/'
                      ? 'bg-gradient-to-r from-primary-700 to-primary-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <FaHome />
                  خانه
                </Link>

                {/* 2. Login/Dashboard Button - Mobile */}
                {isLoggedIn ? (
                  <Link
                    to="/user/dashboard"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all bg-gradient-to-r from-accent-600 to-accent-500 text-white shadow-md"
                    data-testid="dashboard-btn-mobile"
                  >
                    <FaUser />
                    داشبورد
                  </Link>
                ) : (
                  <Link
                    to="/user/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all bg-gradient-to-r from-accent-600 to-accent-500 text-white shadow-md"
                    data-testid="login-btn-mobile"
                  >
                    <FaSignInAlt />
                    ورود
                  </Link>
                )}

                {/* 3. Download App Button - Mobile - Only show in browser mode */}
                {!isPWA && (
                  <Link
                    to="/download-app"
                    onClick={() => setIsMenuOpen(false)}
                    className={`flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all ${
                      location.pathname === '/download-app'
                        ? 'bg-gradient-to-r from-secondary-600 to-secondary-500 text-gray-900 shadow-md'
                        : 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-gray-900 hover:from-secondary-600 hover:to-secondary-700'
                    }`}
                    data-testid="download-app-btn-mobile"
                  >
                    <FaDownload />
                    دانلود اپلیکیشن
                  </Link>
                )}

                {/* 4. Festival Link */}
                <Link
                  to="/festivals"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all ${
                    location.pathname === '/festivals'
                      ? 'bg-gradient-to-r from-primary-700 to-primary-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <FaTrophy />
                  جشنواره‌ها
                </Link>

                {/* 5. Novayar Link - After Festivals */}
                <Link
                  to="/novayar"
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center gap-3 px-6 py-4 rounded-xl font-bold text-lg transition-all ${
                    location.pathname === '/novayar'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 dark:hover:from-gray-700 dark:hover:to-gray-700'
                  }`}
                  data-testid="novayar-btn-mobile"
                >
                  <span className="text-2xl">🤖</span>
                  دستیار هوشمند نوآیار
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}

export default Header;